import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Trash2, Plus } from "lucide-react";

export default function History() {
  const [scans, setScans] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/scans")
      .then((r) => { if (!cancelled) setScans(r.data); })
      .catch(() => { if (!cancelled) setScans([]); });
    return () => { cancelled = true; };
  }, []);

  const reload = () => api.get("/scans").then((r) => setScans(r.data)).catch(() => setScans([]));

  const del = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this scan?")) return;
    await api.delete(`/scans/${id}`);
    reload();
  };

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)]">
      <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="notion-emoji-box callout-yellow">📚</span>
              <h1 className="font-serif-h text-3xl md:text-4xl font-semibold tracking-tight">Your scans</h1>
            </div>
            <p className="text-[#787774] ml-[52px]">All your past audits, ready to reopen.</p>
          </div>
          <Link to="/dashboard" data-testid="new-scan-cta" className="bg-[#2383E2] text-white font-medium px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#1A6FCC] w-max text-sm">
            <Plus className="w-4 h-4" /> New scan
          </Link>
        </div>

        {scans === null && <div className="text-[#9B9A97] text-sm">Loading…</div>}
        {scans && scans.length === 0 && (
          <div className="border border-dashed border-[#EBEBEA] rounded-lg p-12 text-center" data-testid="empty-history">
            <span className="text-4xl block mb-3">📭</span>
            <p className="text-[#787774] mb-4">No scans yet.</p>
            <Link to="/dashboard" className="text-[#2383E2] hover:underline font-medium">Run your first scan →</Link>
          </div>
        )}
        {scans && scans.length > 0 && (
          <div className="border border-[#EBEBEA] rounded-lg divide-y divide-[#EBEBEA] overflow-hidden" data-testid="history-list">
            {scans.map((s) => (
              <Link key={s.id} to={`/scan/${s.id}`} data-testid={`history-item-${s.id}`} className="hover:bg-[#FAFAF9] p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-[#9B9A97] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.url}</div>
                    <div className="text-xs text-[#9B9A97] mt-0.5 flex gap-3 flex-wrap">
                      <span>{new Date(s.created_at).toLocaleString()}</span>
                      {s.target_keyword && <span>Target: <span className="text-[#787774]">{s.target_keyword}</span></span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <ScorePill label="SEO" value={s.seo_score} />
                  <ScorePill label="GEO" value={s.geo_score} />
                  <button data-testid={`delete-scan-${s.id}`} onClick={(e) => del(s.id, e)} className="text-[#9B9A97] hover:text-[#E03E3E] p-2 rounded-md hover:bg-[#FFE2DD] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScorePill({ label, value }) {
  const color = value >= 80 ? "callout-green text-[#0F7B0F]" : value >= 60 ? "callout-orange text-[#B25E00]" : "callout-red text-[#B0261D]";
  return (
    <div className={`${color} rounded-md px-2.5 py-1 text-center min-w-[52px]`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
